import { gql } from '@apollo/client'
import classNames from 'classnames'
import { useContext, useEffect, useRef } from 'react'

import { useChooseFrequencyLaterEvents } from '../../../hooks/use-choose-frequency-later-events'
import { getCardsByFrequency } from '../../../utils'
import { ChooseFrequencyLaterSteps } from '../../choose-frequency-later-steps'
import { ChooseFrequencyLaterStepsContent } from '../../choose-frequency-later-steps/components'
import {
  ChooseFrequencyLaterContextProvider,
  useChooseOfferFrequencyLaterContext,
} from '../../choose-frequency-later-steps/context-provider'
import { ChooseFrequencyLaterModalSteps } from '../../choose-frequency-later-steps/types'
import { BoxLockContext } from '../../context'
import { ModalLock } from '../../dynamic-multiple-offers-switch-frequency-lock/components'
import switchFrequencyStyles from '../../dynamic-multiple-offers-switch-frequency-lock/dynamic-multiple-offers-switch-frequency-lock.module.scss'
import { PaywallLock } from '../../paywall-lock'
import {
  ChooseFrequencyLaterLock_OfferLockDataFragment,
  ChooseFrequencyLaterLock_ViewerFragment,
} from './__generated__/two-step.graphql'

export interface ChooseFrequencyLaterLockProps {
  offerLockData: ChooseFrequencyLaterLock_OfferLockDataFragment
  viewer: ChooseFrequencyLaterLock_ViewerFragment
}

export function ChooseFrequencyLaterLock({ offerLockData, viewer }: ChooseFrequencyLaterLockProps) {
  const { componentType } = useContext(BoxLockContext)

  if (componentType === 'paywall') {
    return (
      <PaywallLock offerLockData={offerLockData}>
        <ChooseFrequencyLaterSteps
          offerLockData={offerLockData}
          viewer={viewer}
          componentType="paywall"
          compactHeader
        />
      </PaywallLock>
    )
  }

  return (
    <ChooseFrequencyLaterContextProvider
      offerLockData={offerLockData}
      componentType="modal"
      compactHeader
    >
      <ChooseFrequencyLaterLockModalContent offerLockData={offerLockData} viewer={viewer} />
    </ChooseFrequencyLaterContextProvider>
  )
}

function ChooseFrequencyLaterLockModalContent({
  offerLockData,
  viewer,
}: ChooseFrequencyLaterLockProps) {
  const { currentStep } = useChooseOfferFrequencyLaterContext()
  const { sendModalClosedEvent } = useChooseFrequencyLaterEvents()
  const isSecondStep = currentStep === ChooseFrequencyLaterModalSteps.SELECT_FREQUENCY

  const cards = offerLockData?.body?.cards || []
  const { monthlyCards, yearlyCards } = getCardsByFrequency(cards)
  const isOneOfferByFrequency = Math.max(monthlyCards.length, yearlyCards.length) === 1
  const isThreeOffersByFrequency = Math.max(monthlyCards.length, yearlyCards.length) === 3
  const modalContentClassName = classNames({
    [switchFrequencyStyles.modalContent]: isThreeOffersByFrequency,
  })

  const currentStepRef = useRef(currentStep)
  currentStepRef.current = currentStep

  useEffect(() => {
    return () => {
      if (cards[0]) sendModalClosedEvent(currentStepRef.current, cards[0])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const modalOfferLockData = isSecondStep
    ? {
        ...offerLockData,
        header: {
          ...offerLockData.header,
          title: offerLockData.header?.secondStepTitle || offerLockData.header?.title,
        },
      }
    : offerLockData

  return (
    <ModalLock
      offerLockData={modalOfferLockData}
      size={isOneOfferByFrequency ? 'sm' : 'lg'}
      headingSize="lg"
      modalContentClassName={modalContentClassName}
    >
      <ChooseFrequencyLaterStepsContent offerLockData={offerLockData} viewer={viewer} />
    </ModalLock>
  )
}

ChooseFrequencyLaterLock.fragments = {
  offerLockData: gql`
    fragment ChooseFrequencyLaterLock_offerLockData on OfferLockData {
      header {
        title
        secondStepTitle
      }
      ...ModalLock_offerLockData
      ...PaywallLock_offerLockData
      ...ChooseFrequencyLaterSteps_offerLockData
    }
    ${ModalLock.fragments.offerLockData}
    ${PaywallLock.fragments.offerLockData}
    ${ChooseFrequencyLaterSteps.fragments.offerLockData}
  `,
  viewer: gql`
    fragment ChooseFrequencyLaterLock_viewer on Profile {
      ...ChooseFrequencyLaterSteps_viewer
    }
    ${ChooseFrequencyLaterSteps.fragments.viewer}
  `,
}
